import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Home, 
  DollarSign, 
  AlertCircle,
  CheckCircle2,
  Clock,
  Filter,
  Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export default function PayoutsPage() {
  const navigate = useNavigate();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Complete payout modal
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [completing, setCompleting] = useState(false);

  const loadPayouts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/payouts`, { withCredentials: true });
      setPayouts(response.data || []);
    } catch (error) {
      // Error logged: 'Failed to load payouts:', error);
      toast.error('Failed to load payouts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPayouts();
  }, [loadPayouts]);

  const handleCompletePayout = async (e) => {
    e.preventDefault();
    try {
      setCompleting(true);
      await axios.post(`${API}/payouts/${selectedPayout.id}/complete`, {
        payment_method: paymentMethod,
        payment_reference: paymentReference,
        notes: paymentNotes
      }, { withCredentials: true });

      toast.success('Payout completed successfully');
      setCompleteModalOpen(false);
      setSelectedPayout(null);
      setPaymentReference('');
      setPaymentNotes('');
      loadPayouts();
    } catch (error) {
      toast.error('Failed to complete payout');
    } finally {
      setCompleting(false);
    }
  };

  const getFilteredPayouts = () => {
    let filtered = payouts;

    if (filterStatus !== 'all') {
      filtered = filtered.filter(p => p.status === filterStatus);
    }

    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.seller_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.seller?.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.item?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.payment_reference?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'completed':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const isOverdue = (payout) => {
    if (payout.status !== 'pending') return false;
    const createdAt = new Date(payout.created_at);
    const now = new Date();
    const hoursDiff = (now - createdAt) / (1000 * 60 * 60);
    return hoursDiff > 24;
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return `$${amount.toLocaleString()}`;
  };

  const filteredPayouts = getFilteredPayouts();
  const pendingCount = payouts.filter(p => p.status === 'pending').length;
  const overdueCount = payouts.filter(p => isOverdue(p)).length;
  const totalPending = payouts.filter(p => p.status === 'pending').reduce((sum, p) => sum + (p.payout_amount || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
        <p className="text-[#474747]">Loading payouts...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F8]">
      <header className="bg-white border-b border-[#E9E9E9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#474747] mb-4"
          >
            <Home className="w-4 h-4" />
            Back to Dashboard
          </button>

          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-[#474747] mb-4">Payouts</h1>

          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search payouts..."
                className="w-full pl-10 pr-4 py-2 border border-[#E9E9E9] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#C6E76C]/50"
              />
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
          <div className="bg-white rounded-lg border border-[#E9E9E9] p-4 sm:p-6">
            <p className="text-xs sm:text-sm text-gray-500 font-medium">Pending</p>
            <p className="text-2xl sm:text-3xl font-semibold text-[#474747] mt-1 sm:mt-2">{pendingCount}</p>
            <p className="text-xs text-gray-500 mt-1">{formatCurrency(totalPending)}</p>
          </div>

          <div className="bg-white rounded-lg border border-[#E9E9E9] p-4 sm:p-6">
            <p className="text-xs sm:text-sm text-gray-500 font-medium">Overdue</p>
            <p className="text-2xl sm:text-3xl font-semibold text-red-600 mt-1 sm:mt-2">{overdueCount}</p>
            <p className="text-xs text-red-500 mt-1">Action required</p>
          </div>

          <div className="bg-white rounded-lg border border-[#E9E9E9] p-4 sm:p-6">
            <p className="text-xs sm:text-sm text-gray-500 font-medium">Completed</p>
            <p className="text-2xl sm:text-3xl font-semibold text-[#474747] mt-1 sm:mt-2">
              {payouts.filter(p => p.status === 'completed').length}
            </p>
          </div>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              filterStatus === 'all' ? 'bg-[#C6E76C] text-[#474747]' : 'bg-white text-gray-600 border border-[#E9E9E9]'
            }`}
          >
            All ({payouts.length})
          </button>
          <button
            onClick={() => setFilterStatus('pending')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              filterStatus === 'pending' ? 'bg-[#C6E76C] text-[#474747]' : 'bg-white text-gray-600 border border-[#E9E9E9]'
            }`}
          >
            Pending ({pendingCount})
          </button>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden space-y-3" data-testid="payouts-mobile-list">
          {filteredPayouts.length === 0 ? (
            <div className="bg-white rounded-xl border border-[#E9E9E9] p-8 text-center text-gray-500 text-sm">No payouts found</div>
          ) : (
            filteredPayouts.map(payout => (
              <div key={payout.id} className={`bg-white rounded-xl border p-4 ${isOverdue(payout) ? 'border-red-200 bg-red-50' : 'border-[#E9E9E9]'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#474747]">{payout.seller_name || payout.seller?.full_name || 'Unknown'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{formatDate(payout.created_at)}</p>
                  </div>
                  <p className="text-base font-bold text-[#474747] shrink-0">{formatCurrency(payout.payout_amount)}</p>
                </div>
                <div className="flex items-center justify-between mt-2.5">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${getStatusColor(payout.status)}`}>
                    {isOverdue(payout) ? 'overdue' : payout.status}
                  </span>
                  {payout.status === 'pending' && (
                    <Button size="sm" onClick={() => { setSelectedPayout(payout); setCompleteModalOpen(true); }}
                      className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747]">
                      Complete
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="hidden md:block bg-white rounded-xl border border-[#E9E9E9] overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-[#E9E9E9]">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Seller</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E9E9E9]">
              {filteredPayouts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">No payouts found</td>
                </tr>
              ) : (
                filteredPayouts.map(payout => (
                  <tr key={payout.id} className={`hover:bg-gray-50 ${isOverdue(payout) ? 'bg-red-50' : ''}`}>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-[#474747]">{payout.seller_name || payout.seller?.full_name || 'Unknown'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-semibold text-[#474747]">{formatCurrency(payout.payout_amount)}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(payout.status)}`}>
                        {payout.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatDate(payout.created_at)}</td>
                    <td className="px-6 py-4">
                      {payout.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedPayout(payout);
                            setCompleteModalOpen(true);
                          }}
                          className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747]"
                        >
                          Complete
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={completeModalOpen} onOpenChange={setCompleteModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-semibold text-[#474747]">Complete Payout</DialogTitle>
          </DialogHeader>

          {selectedPayout && (
            <form onSubmit={handleCompletePayout} className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm"><span className="font-semibold">Seller:</span> {selectedPayout.seller_name || selectedPayout.seller?.full_name || '—'}</p>
                <p className="text-sm mt-1"><span className="font-semibold">Amount:</span> {formatCurrency(selectedPayout.payout_amount)}</p>
              </div>

              <div>
                <label className="text-sm font-medium block mb-2">Payment Method *</label>
                <select required value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full p-2 border border-[#E9E9E9] rounded-md" data-testid="payment-method-select">
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="payid">PayID</option>
                  <option value="cash">Cash</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium block mb-2">Reference *</label>
                <input type="text" required value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} className="w-full p-2 border border-[#E9E9E9] rounded-md" data-testid="payment-reference-input" />
              </div>

              <div className="flex gap-3">
                <Button type="submit" disabled={completing} className="flex-1 bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747]" data-testid="complete-payout-confirm-button">
                  {completing ? 'Processing...' : 'Complete'}
                </Button>
                <Button type="button" onClick={() => setCompleteModalOpen(false)} variant="outline">Cancel</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
